import React, { useState, useEffect, useCallback, useRef, createContext, useContext, ReactNode } from 'react';
import { toast } from 'react-toastify';
import { captureException } from '@sentry/react';
import { EntityType } from './useAutoSave';
import { localStorageAvailable, safeGetItem, safeSetItem } from '@mediatechgroup/app-version';

const STORAGE_KEY = 'clubcentral_offline_queue';
const MAX_QUEUE_SIZE = 50;

interface QueuedChange {
  id: string;
  entityType: EntityType;
  entityId: string;
  field: string;
  value: any;
  timestamp: number;
  retryCount: number;
}

interface PatchFunction {
  (entityId: string, updates: Record<string, any>): Promise<any>;
}

interface PatchFunctions {
  membership: PatchFunction;
  athlete: PatchFunction;
  attendance: PatchFunction;
}

interface UseOfflineQueueOptions {
  patchFunctions: Partial<PatchFunctions>;
  onSyncComplete?: (syncedCount: number, failedCount: number) => void;
  onSyncError?: (error: Error) => void;
  maxRetries?: number;
}

interface UseOfflineQueueReturn {
  isOnline: boolean;
  wasOffline: boolean;
  queuedChangesCount: number;
  isSyncing: boolean;
  queueChange: (change: Omit<QueuedChange, 'id' | 'timestamp' | 'retryCount'>) => void;
  syncQueue: () => Promise<void>;
  clearQueue: () => void;
  getQueuedChanges: () => QueuedChange[];
}

// Generate unique ID
const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Load queue from localStorage
const loadQueue = (): QueuedChange[] => {
  try {
    const stored = safeGetItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

// Save queue to localStorage
const saveQueue = (queue: QueuedChange[]): void => {
  // A browser that denies storage (an embed iframe) simply has no offline
  // queue; only a write refused by storage that IS there (full) is reported.
  if (!safeSetItem(STORAGE_KEY, JSON.stringify(queue)) && localStorageAvailable()) {
    captureException(new Error('Offline queue could not be saved to localStorage'));
  }
};

export const useOfflineQueue = (options: UseOfflineQueueOptions): UseOfflineQueueReturn => {
  const {
    patchFunctions,
    onSyncComplete,
    onSyncError,
    maxRetries = 3,
  } = options;

  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);
  const [queuedChangesCount, setQueuedChangesCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);

  const queueRef = useRef<QueuedChange[]>(loadQueue());
  const syncingRef = useRef(false);

  // Update count from ref
  const updateCount = useCallback(() => {
    setQueuedChangesCount(queueRef.current.length);
  }, []);

  // Initialize count
  useEffect(() => {
    updateCount();
  }, [updateCount]);

  // Queue a change
  const queueChange = useCallback((change: Omit<QueuedChange, 'id' | 'timestamp' | 'retryCount'>) => {
    // Check queue size limit
    if (queueRef.current.length >= MAX_QUEUE_SIZE) {
      // Remove oldest entry
      queueRef.current.shift();
    }

    // Check if we already have a change for this field
    const existingIndex = queueRef.current.findIndex(
      c => c.entityType === change.entityType &&
           c.entityId === change.entityId &&
           c.field === change.field
    );

    const newChange: QueuedChange = {
      ...change,
      id: generateId(),
      timestamp: Date.now(),
      retryCount: 0,
    };

    if (existingIndex >= 0) {
      // Update existing change with new value
      queueRef.current[existingIndex] = newChange;
    } else {
      // Add new change
      queueRef.current.push(newChange);
    }

    saveQueue(queueRef.current);
    updateCount();
  }, [updateCount]);

  // Get queued changes
  const getQueuedChanges = useCallback((): QueuedChange[] => {
    return [...queueRef.current];
  }, []);

  // Clear queue
  const clearQueue = useCallback(() => {
    queueRef.current = [];
    saveQueue([]);
    updateCount();
  }, [updateCount]);

  // Sync queue to server
  const syncQueue = useCallback(async () => {
    if (syncingRef.current || queueRef.current.length === 0) return;

    syncingRef.current = true;
    setIsSyncing(true);

    let syncedCount = 0;
    let failedCount = 0;
    const failedChanges: QueuedChange[] = [];

    // Group changes by entity type and entity ID for batch processing
    const grouped = queueRef.current.reduce((acc, change) => {
      const key = `${change.entityType}-${change.entityId}`;
      if (!acc[key]) {
        acc[key] = {
          entityType: change.entityType,
          entityId: change.entityId,
          fields: {} as Record<string, any>,
          originalChanges: [] as QueuedChange[],
        };
      }
      // Later changes override earlier ones for same field
      acc[key].fields[change.field] = change.value;
      acc[key].originalChanges.push(change);
      return acc;
    }, {} as Record<string, {
      entityType: EntityType;
      entityId: string;
      fields: Record<string, any>;
      originalChanges: QueuedChange[];
    }>);

    // Process each group
    for (const group of Object.values(grouped)) {
      const patchFn = patchFunctions[group.entityType];

      if (!patchFn) {
        failedChanges.push(...group.originalChanges);
        failedCount += group.originalChanges.length;
        continue;
      }

      try {
        await patchFn(group.entityId, group.fields);
        syncedCount += Object.keys(group.fields).length;
      } catch (err) {
        captureException(err);

        // Handle retry logic
        for (const change of group.originalChanges) {
          if (change.retryCount < maxRetries) {
            failedChanges.push({
              ...change,
              retryCount: change.retryCount + 1,
            });
          } else {
            failedCount++;
          }
        }
      }
    }

    // Update queue with only failed items that can be retried
    queueRef.current = failedChanges;
    saveQueue(failedChanges);
    updateCount();

    setIsSyncing(false);
    syncingRef.current = false;

    // Notify completion
    onSyncComplete?.(syncedCount, failedCount);

    if (syncedCount > 0 && failedCount === 0) {
      toast.success(`Synced ${syncedCount} offline change${syncedCount !== 1 ? 's' : ''}`);
    } else if (syncedCount > 0 && failedCount > 0) {
      toast.warning(`Synced ${syncedCount} changes, ${failedCount} failed`);
    } else if (failedCount > 0) {
      toast.error(`Failed to sync ${failedCount} change${failedCount !== 1 ? 's' : ''}`);
      onSyncError?.(new Error(`Failed to sync ${failedCount} changes`));
    }
  }, [patchFunctions, maxRetries, onSyncComplete, onSyncError, updateCount]);

  // Handle online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (wasOffline) {
        syncQueue();
      }
      setWasOffline(false);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
      toast.warning('You are offline. Changes will be saved when connection is restored.', {
        autoClose: 5000,
        toastId: 'offline-warning',
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [wasOffline, syncQueue]);

  // Sync on mount if there are queued changes and we're online
  useEffect(() => {
    if (isOnline && queueRef.current.length > 0) {
      syncQueue();
    }
  }, [isOnline, syncQueue]);

  return {
    isOnline,
    wasOffline,
    queuedChangesCount,
    isSyncing,
    queueChange,
    syncQueue,
    clearQueue,
    getQueuedChanges,
  };
};

// Context provider for global offline queue access

interface OfflineQueueContextValue extends UseOfflineQueueReturn {}

const OfflineQueueContext = createContext<OfflineQueueContextValue | null>(null);

export const OfflineQueueProvider: React.FC<{
  children: ReactNode;
  patchFunctions: Partial<PatchFunctions>;
}> = ({ children, patchFunctions }) => {
  const offlineQueue = useOfflineQueue({ patchFunctions });

  return (
    <OfflineQueueContext.Provider value={offlineQueue}>
      {children}
    </OfflineQueueContext.Provider>
  );
};

export const useOfflineQueueContext = (): OfflineQueueContextValue => {
  const context = useContext(OfflineQueueContext);
  if (!context) {
    throw new Error('useOfflineQueueContext must be used within an OfflineQueueProvider');
  }
  return context;
};

export default useOfflineQueue;
